import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  HelpCircle,
  Image as ImageIcon,
  Loader2,
  X,
  Search,
  Send,
} from 'lucide-react';
import { apiGet, apiUpload } from '../shared/apiClient';
import type { Impianto, Segnalazione } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { compressImages } from '../utils/imageCompress';
import { MAX_FOTO_SEGNALAZIONE, toggleMultiOpzione, isMultiOpzioneSelected } from '../shared/constants';
import {
  AvvisoDoppioniImpiantoSquadra,
  useAttivitaAperteImpiantoSquadra,
} from './AvvisoDoppioniImpiantoModal';

interface NuovaSegnalazioneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (nuova: Segnalazione) => void;
  impiantoPreselezionato?: Impianto | null;
}

export function NuovaSegnalazioneModal({
  isOpen,
  onClose,
  onSuccess,
  impiantoPreselezionato = null,
}: NuovaSegnalazioneModalProps) {
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [impiantoScelto, setImpiantoScelto] = useState<Impianto | null>(impiantoPreselezionato);

  // Controllo doppioni unificato (chiamate attive/sospese + segnalazioni aperte) su impianto selezionato
  const attivitaImpianto = useAttivitaAperteImpiantoSquadra(impiantoScelto);

  // Opzioni anomalie dinamiche da /chiamate/liste-opzioni
  const [opzioniGuasto, setOpzioniGuasto] = useState<string[]>([
    'lampeggio',
    'spento',
    'lanterna urtata/girata',
    'palo a sbraccio girato',
    'centralino abbattuto',
    'pulsante guasto/rotto',
    'dispositivo non vedente guasto',
  ]);

  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setDebouncedSearch('');
      if (!impiantoPreselezionato) {
        setImpiantoScelto(null);
      }
      return;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, impiantoPreselezionato]);

  // Carica le liste opzioni all'apertura
  useEffect(() => {
    if (!isOpen) return;
    apiGet<{ guastiSegnalati?: string[] }>('/chiamate/liste-opzioni')
      .then((res) => {
        if (Array.isArray(res.data?.guastiSegnalati) && res.data.guastiSegnalati.length > 0) {
          setOpzioniGuasto(res.data.guastiSegnalati);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  // Se fornito impianto preselezionato, impostalo
  useEffect(() => {
    if (impiantoPreselezionato) {
      setImpiantoScelto(impiantoPreselezionato);
    }
  }, [impiantoPreselezionato]);

  // Ricerca con debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const MAX_RISULTATI_VISIBILI = 40;

  const impiantiQuery = useQuery({
    queryKey: ['impianti-search', debouncedSearch],
    enabled: isOpen && !impiantoScelto && debouncedSearch.trim().length >= 2,
    queryFn: async () => {
      const res = await apiGet<Impianto[]>('/impianti', { search: debouncedSearch.trim() });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const filtered = useMemo(() => {
    if (!Array.isArray(impiantiQuery.data)) return [];
    return impiantiQuery.data;
  }, [impiantiQuery.data]);

  const displayedImpianti = useMemo(() => {
    return filtered.slice(0, MAX_RISULTATI_VISIBILI);
  }, [filtered]);

  const handleSelectImpianto = (imp: Impianto) => {
    setImpiantoScelto(imp);
    setSearch('');
  };

  const handleClearSelection = () => {
    setImpiantoScelto(null);
    setSearch('');
  };

  if (!isOpen || typeof document === 'undefined') return null;

  const handleAddPhotos = async (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    setCompressing(true);
    try {
      const compressi = await compressImages(newFiles);
      setFotoFiles((prev) => [...prev, ...compressi].slice(0, MAX_FOTO_SEGNALAZIONE));
    } catch {
      setFotoFiles((prev) => [...prev, ...newFiles].slice(0, MAX_FOTO_SEGNALAZIONE));
    } finally {
      setCompressing(false);
    }
  };

  const handleRemovePhoto = (idx: number) => {
    setFotoFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!impiantoScelto) {
      toast({ type: 'error', message: 'Seleziona prima un impianto' });
      return;
    }
    if (!titolo.trim()) {
      toast({ type: 'error', message: 'Indica il tipo di guasto o anomalia riscontrata' });
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('impiantoId', impiantoScelto.id);
      fd.append('titolo', titolo.trim());
      if (descrizione.trim()) fd.append('descrizione', descrizione.trim());
      for (const f of fotoFiles) {
        fd.append('foto', f);
      }

      const res = await apiUpload<Segnalazione>('/segnalazioni', fd);
      toast({ type: 'success', message: 'Segnalazione inviata con successo alla sala operativa' });
      onSuccess(res.data);
      onClose();
    } catch (err: any) {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Errore durante l\'invio della segnalazione' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Chip selezionati da titolo
  const chipSelezionati = titolo
    ? titolo.split(/\s*-\s*/).map((s) => s.trim()).filter(Boolean)
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/80 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header fisso */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                Nuova Segnalazione Guasto
              </h3>
              <p className="text-xs text-slate-500">
                Rilevamento anomalie e difformità su impianto
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition active:scale-95"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form scrollabile */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* STEP 1: Selezione Impianto */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Impianto interessato <span className="text-rose-500">*</span>
            </label>

            {impiantoScelto ? (
              <div className="rounded-2xl border-2 border-emerald-500 bg-linear-to-br from-emerald-50/90 to-teal-50/50 p-4 shadow-2xs">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 uppercase tracking-wider">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Impianto Confermato
                    </div>
                    <div className="font-extrabold text-slate-900 text-base sm:text-lg">
                      {impiantoScelto.codice} · {impiantoScelto.nome}
                    </div>
                    {impiantoScelto.tipo && (
                      <div className="text-xs text-emerald-800 font-medium">Tipologia: {impiantoScelto.tipo}</div>
                    )}
                  </div>
                  {!impiantoPreselezionato && (
                    <button
                      type="button"
                      className="btn-outline px-3 py-1.5 text-xs bg-white flex items-center gap-1 shrink-0 font-bold hover:bg-slate-50 border-slate-300"
                      onClick={handleClearSelection}
                    >
                      <X className="w-3.5 h-3.5" /> Cambia
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <Search className="w-4 h-4" />
                  </div>
                  <input
                    className="input !pl-11 !pr-11 font-semibold text-xs sm:text-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Digita codice o nome (es. 01001 o Via Roma)..."
                    autoFocus
                  />
                  {search && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                      onClick={() => setSearch('')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {search.trim().length < 2 ? (
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-500 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" />
                    <span>Digita almeno 2 caratteri per avviare la ricerca automatica.</span>
                  </div>
                ) : impiantiQuery.isLoading ? (
                  <div className="space-y-2">
                    <div className="skeleton h-14" />
                    <div className="skeleton h-14" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-600 text-center font-medium">
                    Nessun impianto trovato per "{search}".
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[260px] overflow-auto pr-1">
                    <div className="flex items-center justify-between text-xs text-slate-500 font-semibold px-1 pb-1">
                      <span>Risultati trovati ({filtered.length}):</span>
                      {filtered.length > MAX_RISULTATI_VISIBILI && (
                        <span className="text-[11px] text-amber-600 font-bold bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                          Mostrati i primi {MAX_RISULTATI_VISIBILI} (affina la ricerca)
                        </span>
                      )}
                    </div>
                    {displayedImpianti.map((imp) => (
                      <button
                        key={imp.id}
                        type="button"
                        className="w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-emerald-500 hover:bg-emerald-50/40 active:bg-emerald-100/60 p-3.5 transition flex items-center justify-between gap-3 shadow-2xs group"
                        onClick={() => handleSelectImpianto(imp)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-sm text-slate-900 group-hover:text-emerald-800 break-words leading-snug">
                            {imp.codice || 'N/D'} · {imp.nome || 'Senza nome'}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {imp.tipo || 'Impianto standard'}
                          </div>
                        </div>
                        <span className="badge-outline text-[11px] font-bold text-slate-500 shrink-0 group-hover:text-emerald-700">
                          Seleziona →
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* BANNER + POPUP ANTI-DOPPIONE UNIFICATO (CHIAMATE + SEGNALAZIONI) */}
          {impiantoScelto && (
            <AvvisoDoppioniImpiantoSquadra
              impianto={impiantoScelto}
              attivita={attivitaImpianto}
              onClearImpianto={handleClearSelection}
              onParentClose={onClose}
              contesto="segnalazione"
            />
          )}

          {/* STEP 2: Tipo di Guasto / Anomalia (Select a comparsa + Chip) */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Tipo di Guasto / Anomalia <span className="text-rose-500">*</span>
            </label>

            {/* Select a comparsa per scelte rapide */}
            <div className="relative">
              <select
                className="input text-xs font-medium bg-white cursor-pointer"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setTitolo(toggleMultiOpzione(titolo, e.target.value));
                  }
                }}
              >
                <option value="">＋ Scegli anomalia frequente dal menu…</option>
                {opzioniGuasto.map((opz) => (
                  <option key={opz} value={opz}>
                    {isMultiOpzioneSelected(titolo, opz) ? `✓ ${opz}` : opz}
                  </option>
                ))}
              </select>
            </div>

            {/* Chip selezionati */}
            {chipSelezionati.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {chipSelezionati.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-100 text-amber-900 text-xs font-bold border border-amber-200"
                  >
                    <span>{c}</span>
                    <button
                      type="button"
                      onClick={() => setTitolo(toggleMultiOpzione(titolo, c))}
                      className="p-0.5 hover:bg-amber-200 rounded text-amber-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input testuale sintetico per personalizzazione */}
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Oppure scrivi sintesi anomalia (es. lanterna girata - spira tranciata)..."
              className="input text-xs font-medium mt-1"
            />
          </div>

          {/* STEP 3: Descrizione libera */}
          <div className="space-y-1">
            <label className="block text-xs font-bold text-slate-700">
              Descrizione dettagliata <span className="text-slate-400 font-normal">(opzionale)</span>
            </label>
            <textarea
              className="input min-h-[85px] resize-y text-xs font-medium"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Dettagli aggiuntivi: posizione palo, direzione lanterna, componente da sostituire..."
            />
          </div>

          {/* STEP 4: Foto del disservizio (Max 5) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-700">
                Foto del disservizio / anomalia
              </label>
              <span className="text-[11px] font-semibold text-slate-500">
                {fotoFiles.length}/{MAX_FOTO_SEGNALAZIONE} allegate
              </span>
            </div>

            {/* Griglia foto caricate */}
            {fotoFiles.length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {fotoFiles.map((file, idx) => (
                  <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group bg-slate-100">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="anteprima"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(idx)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white hover:bg-rose-600 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Pulsanti Fotocamera & Galleria */}
            {fotoFiles.length < MAX_FOTO_SEGNALAZIONE && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <label
                  className={`btn-outline py-2.5 px-3 flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                    compressing ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <Camera className="w-4 h-4 text-emerald-600" />
                  <span>Fotocamera</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) handleAddPhotos(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                </label>

                <label
                  className={`btn-outline py-2.5 px-3 flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                    compressing ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <ImageIcon className="w-4 h-4 text-sky-600" />
                  <span>Galleria</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) handleAddPhotos(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}

            {compressing && (
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                <span>Compressione immagini in corso…</span>
              </div>
            )}
          </div>

          {/* Footer bottoni */}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn-outline px-4 py-2.5 text-xs font-bold"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !impiantoScelto || !titolo.trim()}
              className="btn-primary px-5 py-2.5 text-xs font-bold gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Invio in corso…</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Invia Segnalazione</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
